import React, { useState } from 'react';
import { ButtonV2, CopyButton, LinkIcon } from '@asigna/kit';
import cx from 'classnames';
import { ArrowDownIcon, WalletWithIcons } from 'components';
import { PERMISSION_SIGNING_MESSAGE } from 'constants/base';
import { useReadOnlyWarning } from 'hooks';
import useAddress from 'hooks/use-address';
import { useBreakpoints } from 'hooks/use-breakpoints';
import useNetwork from 'hooks/use-network';
import DeniedIcon from 'images/denied.svg';
import GrantedIcon from 'images/granted.svg';
import PendingIcon from 'images/pending_icon.svg';
import SignedIcon from 'images/signed_message_icon.svg';
import { useCurrentSafe } from 'store';
import { IEditPermission, PermissionType } from 'types';
import { getAccountExplorerUrl, shortenAddress, shortenText } from 'utils/helper';

// TODO_R: move into HistoryTransactionsTab/components/
// TODO_R: заменить @mui на makeStyles с @material-ui
export const MessageView = (props: {
  messageToSign: IEditPermission;
  onSign: (message: string) => void;
}) => {
  const { messageToSign, onSign } = props;
  const { currentSafe } = useCurrentSafe();
  const walletAccount = useAddress() || '';
  const readOnlyWarning = useReadOnlyWarning();
  const [network] = useNetwork();
  const { onMobileZone } = useBreakpoints();

  const [isOpenDetails, setOpenDetails] = useState(false);
  const isSigned =
    messageToSign.signatures.some((signature) => signature.userAddress === walletAccount) ||
    messageToSign.signatures.length >= currentSafe.threshold;

  const messageData =
    messageToSign.message.indexOf(PERMISSION_SIGNING_MESSAGE) !== -1
      ? JSON.parse(messageToSign.message.slice(30))
      : [];

  if (onMobileZone) {
    return (
      <div className="flex flex-col text-regular-fa items-start px-20 py-16 gap-y-[8px] w-full">
        <div className="flex justify-between gap-4 w-full items-start">
          <div
            className={cx(
              'classes.key',
              'classes.tableItem',
              'isOpenDetails && classes.tableDetailsItem'
            )}
          >
            {!isOpenDetails ? (
              <span className="text-r12">
                {shortenText(messageToSign.message, messageToSign.message.length > 40 ? 30 : 40)}
              </span>
            ) : (
              <>
                {messageToSign.message.indexOf(PERMISSION_SIGNING_MESSAGE) !== -1 ? (
                  <span className="text-r12">{PERMISSION_SIGNING_MESSAGE}</span>
                ) : (
                  <span className="text-r12">{messageToSign.message}</span>
                )}
              </>
            )}
          </div>
          <div className="text-r12 text-white flex items-center gap-4 shrink-0 text-r13">
            {isSigned ? (
              <>
                <img src={SignedIcon} alt="" />
                Signed
              </>
            ) : (
              <>
                <img src={PendingIcon} alt="" />
                Pending
              </>
            )}
          </div>
        </div>
        <div className="flex gap-4 w-full">
          {Boolean(messageToSign.message) && messageToSign.message.length > 40 && (
            <>
              {isOpenDetails ? (
                <>
                  {messageToSign.message.indexOf(PERMISSION_SIGNING_MESSAGE) !== -1 ? (
                    <div className="mb-20">
                      <div className="flex flex-col items-start gap-20">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDetails(false);
                          }}
                          className="flex items-center gap-4"
                        >
                          <span className="text-m14 text-primary">hide</span>
                          <ArrowDownIcon className="w-12 h-12 rotate-180" />
                        </button>
                      </div>
                      <div className="grid grid-cols-[1fr_66px_58px_44px] text-m12 text-regular gap-4 mt-20">
                        <span>Address</span>
                        <span>
                          Execute
                          <br />
                          Txn
                        </span>
                        <span>
                          Create
                          <br />
                          Txn
                        </span>
                        <span>
                          Sign
                          <br />
                          Txn
                        </span>
                      </div>
                      {messageData.permissionsData.map(
                        (item: {
                          address: string;
                          processType: string;
                          permissions: PermissionType[];
                        }) => {
                          return (
                            <div
                              key={item.address}
                              className="grid grid-cols-[1fr_66px_58px_44px] text-m12 items-center mt-16 gap-4"
                            >
                              <div className="flex items-center gap-4 sm:gap-8 mr-16">
                                {shortenAddress(item.address)}
                                <CopyButton copy={item.address} />
                                <LinkIcon href={getAccountExplorerUrl(item.address, network)} />
                              </div>
                              <img
                                src={
                                  item.permissions.includes('EXECUTE') ? GrantedIcon : DeniedIcon
                                }
                                alt=""
                              />
                              <img
                                src={
                                  item.permissions.includes('PROPOSE') ? GrantedIcon : DeniedIcon
                                }
                                alt=""
                              />
                              <img
                                src={item.permissions.includes('VOTE') ? GrantedIcon : DeniedIcon}
                                alt=""
                              />
                            </div>
                          );
                        }
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDetails(false);
                      }}
                      className="flex items-center gap-4"
                    >
                      <span className="text-m14 text-primary">hide</span>
                      <ArrowDownIcon className="w-12 h-12 rotate-180" />
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDetails(true);
                  }}
                  className="flex items-center gap-4"
                >
                  <span className="text-m14 text-primary">more</span>
                  <ArrowDownIcon className="w-12 h-12" />
                </button>
              )}
            </>
          )}
        </div>
        <div className="flex items-end justify-between gap-4 w-full">
          <span
            className={cx(
              'flex gap-8 items-center justify-self-start px-8 py-4 rounded-[4px]',
              messageToSign.signatures.length === currentSafe.threshold
                ? 'text-success bg-success-10'
                : 'text-primary bg-primary-10',
              'col-start-1 sm:col-auto row-start-2 sm:row-auto'
            )}
          >
            <span className="text-r12 col-start-2 sm:col-auto row-start-1 sm:row-auto">
              {messageToSign.signatures.length}/{currentSafe.threshold} confirmations
            </span>
          </span>
          <ButtonV2 message="Details" size="sm" className="block !py-0" color="secondary" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx(
        'grid grid-cols-[35%_20%_20%_15%_10%] justify-between sm:justify-normal items-start gap-y-[8px] sm:gap-y-0',
        'text-regular-fa hover:bg-white-3 active:bg-white-5 px-20 py-16 sm:px-32 w-full'
      )}
    >
      <div
        className={cx(
          'col-start-1 sm:col-auto row-start-1 sm:row-auto',
          'flex gap-4 items-center pt-8'
        )}
      >
        {!isOpenDetails && (
          <span className="text-r12 sm:text-r14">
            {shortenText(messageToSign.message, messageToSign.message.length > 40 ? 30 : 40)}
          </span>
        )}
        {Boolean(messageToSign.message) && messageToSign.message.length > 40 && (
          <>
            {isOpenDetails ? (
              <>
                {messageToSign.message.indexOf(PERMISSION_SIGNING_MESSAGE) !== -1 && (
                  <div className="mb-20">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-20">
                      <span className="text-r12 sm:text-r14">{PERMISSION_SIGNING_MESSAGE}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDetails(false);
                        }}
                        className="flex items-center gap-4"
                      >
                        <span className="text-m14 text-primary">hide</span>
                        <ArrowDownIcon className="w-12 h-12 rotate-180" />
                      </button>
                    </div>
                    <div className="grid grid-cols-[1fr_76px_68px_54px] text-m12 text-regular gap-8 mt-20">
                      <span>Address</span>
                      <span>Execute Txn</span>
                      <span>Create Txn</span>
                      <span>Sign Txn</span>
                    </div>
                    {messageData.permissionsData.map(
                      (item: {
                        address: string;
                        processType: string;
                        permissions: PermissionType[];
                      }) => {
                        return (
                          <div
                            key={item.address}
                            className="grid grid-cols-[1fr_76px_68px_54px] text-m12 items-center mt-16 gap-8"
                          >
                            <div className="flex items-center gap-4 sm:gap-8 mr-16 sm:mr-32">
                              {shortenAddress(item.address)}
                              <CopyButton copy={item.address} />
                              <LinkIcon href={getAccountExplorerUrl(item.address, network)} />
                            </div>
                            <img
                              src={item.permissions.includes('EXECUTE') ? GrantedIcon : DeniedIcon}
                              alt=""
                            />
                            <img
                              src={item.permissions.includes('PROPOSE') ? GrantedIcon : DeniedIcon}
                              alt=""
                            />
                            <img
                              src={item.permissions.includes('VOTE') ? GrantedIcon : DeniedIcon}
                              alt=""
                            />
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDetails(true);
                }}
                className="flex items-center gap-4"
              >
                <span className="text-m14 text-primary">more</span>
                <ArrowDownIcon className="w-12 h-12" />
              </button>
            )}
          </>
        )}
      </div>
      <span
        className={cx(
          'flex gap-8 items-center justify-self-start py-4 rounded-[4px] mt-[6px]',
          'col-start-1 sm:col-auto row-start-2 sm:row-auto'
        )}
      >
        <WalletWithIcons address={messageToSign.creator} />
      </span>
      <span
        className={cx(
          'flex gap-8 items-center justify-self-start px-8 py-4 rounded-[4px] mt-[6px]',
          messageToSign.signatures.length === currentSafe.threshold
            ? 'text-success bg-success-10'
            : 'text-primary bg-primary-10',
          'col-start-1 sm:col-auto row-start-2 sm:row-auto'
        )}
      >
        <span className="text-r12 col-start-2 sm:col-auto row-start-1 sm:row-auto">
          {messageToSign.signatures.length}/{currentSafe.threshold} confirmations
        </span>
      </span>

      <div
        className={cx(
          'col-start-2 sm:col-auto row-start-1 sm:row-auto',
          'text-r12 text-white flex items-center gap-4 mt-[10px]'
        )}
      >
        {isSigned ? (
          <>
            <img src={SignedIcon} alt="" />
            Signed
          </>
        ) : (
          <>
            <img src={PendingIcon} alt="" />
            Pending
          </>
        )}
      </div>
      <div
        className={cx(
          'classes.tableItem',
          'classes.buttonWrapper',
          'col-start-2 sm:col-auto row-start-2 sm:row-auto'
        )}
      >
        {!isSigned && !messageToSign.exported && (
          <ButtonV2
            onClick={async (event) => {
              event.stopPropagation();
              onSign(messageToSign.message);
            }}
            disabled={Boolean(readOnlyWarning)}
            disableHint={readOnlyWarning}
            hintPosition="bottom-end"
            message="Sign"
            size="md"
            className="!py-0 min-w-[86px]"
          />
        )}
      </div>
    </div>
  );
};
